import { ArrowDown, ArrowRight, Brain, Check, Database, Inbox, LayoutDashboard, MousePointer2, Workflow } from "lucide-react";
import { FormEvent, useCallback, useEffect, useRef, useState } from "react";
import { SiteNav } from "../components/SiteNav";
import { TurnstileWidget } from "../components/TurnstileWidget";
import { useI18n } from "../i18n/i18n";
import { homeMessages, sharedMessages } from "../i18n/messages";
import { usePageMeta } from "../lib/meta";

function whatsappHref() {
  const encodedDigits = [44, 51, 46, 52, 50, 44, 43, 45, 46, 50, 44, 44];
  const phone = encodedDigits.map((value, index) => String.fromCharCode(value + 7 - (index % 4))).join("");
  return `https://wa.me/${phone}`;
}

type ContactDraft = {
  name: string;
  email: string;
  focus: string;
  message: string;
};

const emptyDraft: ContactDraft = { name: "", email: "", focus: "website", message: "" };
const turnstileSiteKey = import.meta.env.VITE_TURNSTILE_SITE_KEY ?? (import.meta.env.DEV ? "1x00000000000000000000AA" : "");

type SubmissionStatus = "idle" | "sending" | "success" | "error";

function WhatsAppIcon() {
  return <svg viewBox="0 0 24 24" aria-hidden="true"><path fill="currentColor" d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.472-.148-.67.149-.198.297-.767.967-.94 1.164-.173.198-.347.223-.644.074-.297-.149-1.255-.463-2.39-1.477-.883-.788-1.479-1.761-1.653-2.058-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.372-.025-.52-.074-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.017-.372-.021-.57-.021-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.463 1.065 2.877 1.213 3.076.148.198 2.095 3.2 5.077 4.49.709.306 1.262.489 1.693.626.711.226 1.358.194 1.869.118.57-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.005a9.867 9.867 0 0 1-5.032-1.378l-.361-.214-3.741.982.998-3.648-.235-.375a9.868 9.868 0 0 1-1.51-5.26c.002-5.447 4.425-9.87 9.875-9.87 2.642.001 5.128 1.03 6.996 2.899a9.825 9.825 0 0 1 2.896 7.002c-.002 5.448-4.426 9.87-9.876 9.87m8.384-18.389A11.819 11.819 0 0 0 12.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.548 4.142 1.588 5.95L0 24l6.335-1.662a11.867 11.867 0 0 0 5.709 1.453h.005c6.554 0 11.89-5.336 11.893-11.893a11.82 11.82 0 0 0-3.507-8.402" /></svg>;
}

function CategoryIllustration({ kind }: { kind: "websites" | "automations" }) {
  if (kind === "websites") {
    return (
      <div className="category-visuals category-visuals-websites" aria-hidden="true">
        <div className="category-visual-panel category-browser-preview">
          <div className="category-browser-bar"><span /><span /><span /><b>WEB</b></div>
          <div className="category-browser-content">
            <div className="category-browser-image"><span /></div>
            <div className="category-browser-copy"><i /><i /><i /></div>
            <span className="category-browser-action"><MousePointer2 /></span>
          </div>
        </div>
        <div className="category-visual-panel category-mobile-preview">
          <span className="category-phone-island" />
          <div className="category-phone-screen">
            <div className="category-mobile-top"><b>W</b><i /></div>
            <div className="category-mobile-image" />
            <div className="category-mobile-copy"><i /><i /></div>
            <span className="category-mobile-action"><ArrowRight /></span>
          </div>
          <span className="category-phone-home" />
        </div>
      </div>
    );
  }
  return (
    <div className="category-visuals category-visuals-automations" aria-hidden="true">
      <div className="category-visual-panel category-workflow-preview">
        <div className="category-workflow-head"><Workflow /><b>FLOW</b><span>LIVE</span></div>
        <div className="category-workflow-map">
          <span className="category-workflow-node category-workflow-in"><Inbox /></span>
          <i className="category-workflow-line category-workflow-line-one" />
          <span className="category-workflow-node category-workflow-process"><Workflow /></span>
          <i className="category-workflow-line category-workflow-line-two" />
          <span className="category-workflow-node category-workflow-out"><Database /></span>
        </div>
      </div>
      <div className="category-visual-panel category-dashboard-preview">
        <div className="category-dashboard-head"><LayoutDashboard /><b>OPS</b></div>
        <div className="category-dashboard-metric"><strong>84%</strong><span><i /></span></div>
        <ul><li><Check /><span /></li><li><Check /><span /></li><li><Check /><span /></li></ul>
      </div>
      <div className="category-ai-preview">
        <Brain />
        <strong>AI</strong>
      </div>
    </div>
  );
}

type FocusOption = { value: string; label: string };

function ProjectFocusSelect({ value, options, labelId, onChange }: { value: string; options: FocusOption[]; labelId: string; onChange: (value: string) => void }) {
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const selected = options.find((option) => option.value === value) ?? options[0];

  useEffect(() => {
    if (!open) return;
    const closeOnOutsidePress = (event: PointerEvent) => {
      if (!containerRef.current?.contains(event.target as Node)) setOpen(false);
    };
    document.addEventListener("pointerdown", closeOnOutsidePress);
    return () => document.removeEventListener("pointerdown", closeOnOutsidePress);
  }, [open]);

  const choose = (nextValue: string) => {
    onChange(nextValue);
    setOpen(false);
    window.setTimeout(() => triggerRef.current?.focus(), 0);
  };

  return (
    <div className="contact-focus-select" ref={containerRef}>
      <input name="focus" type="hidden" value={value} />
      <button
        ref={triggerRef}
        className="contact-focus-trigger"
        type="button"
        aria-labelledby={labelId}
        aria-haspopup="listbox"
        aria-expanded={open}
        onClick={() => setOpen((current) => !current)}
        onKeyDown={(event) => {
          if (event.key === "Escape") setOpen(false);
          if (event.key === "ArrowDown" || event.key === "ArrowUp") {
            event.preventDefault();
            setOpen(true);
          }
        }}
      >
        <span>{selected.label}</span>
      </button>
      {open && (
        <div className="contact-focus-options" role="listbox" aria-labelledby={labelId}>
          {options.map((option) => (
            <button
              key={option.value}
              type="button"
              role="option"
              aria-selected={option.value === value}
              className={option.value === value ? "selected" : ""}
              onClick={() => choose(option.value)}
              onKeyDown={(event) => {
                if (event.key === "Escape") {
                  setOpen(false);
                  triggerRef.current?.focus();
                }
              }}
            >
              {option.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

export function Home() {
  const { language, text } = useI18n();
  const [draft, setDraft] = useState<ContactDraft>(emptyDraft);
  const [submissionStatus, setSubmissionStatus] = useState<SubmissionStatus>("idle");
  const [turnstileToken, setTurnstileToken] = useState<string | null>(null);
  const [turnstileVersion, setTurnstileVersion] = useState(0);
  const isItalian = language === "it";
  const title = isItalian ? "Mattia D’Elia — Siti Web, Sistemi Aziendali & Automazioni" : "Mattia D’Elia — Websites, Business Systems & Automation";
  const description = isItalian
    ? "Siti web, sistemi aziendali, automazioni e AI applicata costruiti intorno alle reali esigenze della tua attività."
    : "Custom websites, business systems, workflow automation and applied AI designed around real business needs.";
  usePageMeta(title, description, "/");

  useEffect(() => {
    let frame = 0;

    const scrollToContact = () => {
      if (window.location.hash !== "#contact") return;
      window.cancelAnimationFrame(frame);
      frame = window.requestAnimationFrame(() => {
        document.getElementById("contact")?.scrollIntoView({ behavior: "smooth", block: "start" });
      });
    };

    scrollToContact();
    window.addEventListener("hashchange", scrollToContact);
    return () => {
      window.cancelAnimationFrame(frame);
      window.removeEventListener("hashchange", scrollToContact);
    };
  }, []);

  const focusOptions: FocusOption[] = [
    { value: "website", label: isItalian ? "Un sito web" : "A website" },
    { value: "system", label: isItalian ? "Un sistema aziendale" : "A business system" },
    { value: "automation", label: isItalian ? "Un’automazione" : "Automation" },
    { value: "ai", label: isItalian ? "Un’integrazione AI" : "AI integration" },
    { value: "other", label: isItalian ? "Altro" : "Something else" },
  ];

  const handleTurnstileError = useCallback(() => setSubmissionStatus("error"), []);
  const handleTurnstileToken = useCallback((token: string | null) => setTurnstileToken(token), []);

  const submitContact = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (submissionStatus === "sending") return;
    if (!turnstileToken) {
      setSubmissionStatus("error");
      return;
    }

    const formData = new FormData(event.currentTarget);
    setSubmissionStatus("sending");

    try {
      const response = await fetch("/api/contact", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          ...draft,
          language,
          turnstileToken,
          website: String(formData.get("website") ?? ""),
        }),
      });

      if (!response.ok) throw new Error("Contact submission failed.");

      setDraft(emptyDraft);
      setSubmissionStatus("success");
      setTurnstileToken(null);
      setTurnstileVersion((current) => current + 1);
    } catch {
      setSubmissionStatus("error");
      setTurnstileToken(null);
      setTurnstileVersion((current) => current + 1);
    }
  };

  return (
    <div className="home-page">
      <SiteNav />
      <main id="main" className="home-main">
        <section className="home-hero" aria-labelledby="home-title">
          <div className="hero-panel">
            <p className="hero-greeting">{text(homeMessages.greeting)}</p>
            <h1 id="home-title">{text(homeMessages.lead)}</h1>
            <p className="hero-intro">{text(homeMessages.body)}</p>
            <div className="hero-actions">
              <a className="home-button dark" href="#contact">{isItalian ? "Parliamo del tuo progetto" : "Discuss your project"} <ArrowDown size={18} /></a>
            </div>
          </div>
        </section>

        <section className="home-paths" aria-label={isItalian ? "Esplora i servizi" : "Explore services"}>
          <a className="home-path-card home-path-websites" href="/websites">
            <CategoryIllustration kind="websites" />
            <div className="home-path-content">
              <h2>{text(sharedMessages.websites)}</h2>
              <p>{isItalian ? "Siti chiari e distintivi, creati per infondere fiducia e trasformare l'interesse in contatti, prenotazioni o vendite." : "Clear, distinctive websites designed to build trust and turn interest into leads, bookings, or sales."}</p>
              <ul className="home-path-tags" aria-label={isItalian ? "Tipi di progetto" : "Project types"}>
                <li>{isItalian ? "Siti aziendali" : "Business sites"}</li><li>{isItalian ? "Prenotazioni" : "Bookings"}</li><li>E-commerce</li>
              </ul>
              <span>{isItalian ? "Scopri i siti web" : "Explore websites"}<ArrowRight size={18} /></span>
            </div>
          </a>
          <a className="home-path-card home-path-automations" href="/automations">
            <CategoryIllustration kind="automations" />
            <div className="home-path-content">
              <h2>{text(sharedMessages.automations)}</h2>
              <p>{isItalian ? "Software su misura, workflow e IA applicata per eliminare il lavoro manuale e connettere strumenti, dati e processi." : "Custom software, workflows, and applied AI that reduce manual work and seamlessly connect your operations."}</p>
              <ul className="home-path-tags" aria-label={isItalian ? "Tipi di soluzione" : "Solution types"}>
                <li>{isItalian ? "Sistemi aziendali" : "Business systems"}</li><li>{isItalian ? "Automazioni" : "Automation"}</li><li>{isItalian ? "AI applicata" : "Applied AI"}</li>
              </ul>
              <span>{isItalian ? "Scopri i sistemi" : "Explore systems"}<ArrowRight size={18} /></span>
            </div>
          </a>
        </section>

        <div className="contact-panel">
          <section className="home-section contact-section-home" id="contact" aria-labelledby="contact-title">
            <div className="contact-section-heading">
              <p className="home-eyebrow">{text(homeMessages.contactEyebrow)}</p>
              <h2 id="contact-title">{text(homeMessages.contactTitle)}</h2>
            </div>
          </section>
          <div className="home-section contact-form-section">
            <form className="home-contact-form" aria-label={isItalian ? "Modulo di contatto" : "Contact form"} aria-busy={submissionStatus === "sending"} onSubmit={submitContact}>
            <label className="contact-honeypot" aria-hidden="true">
              <span>Website</span>
              <input name="website" tabIndex={-1} autoComplete="off" />
            </label>
            <label>
              <span>{isItalian ? "Nome" : "Name"}</span>
              <input autoComplete="name" name="name" minLength={2} maxLength={100} required value={draft.name} onChange={(event) => setDraft((current) => ({ ...current, name: event.target.value }))} />
            </label>
            <label>
              <span>Email</span>
              <input autoComplete="email" name="email" type="email" maxLength={254} required value={draft.email} onChange={(event) => setDraft((current) => ({ ...current, email: event.target.value }))} />
            </label>
            <label className="home-contact-focus">
              <span id="contact-focus-label">{isItalian ? "Di cosa hai bisogno?" : "What do you need?"}</span>
              <ProjectFocusSelect
                value={draft.focus}
                options={focusOptions}
                labelId="contact-focus-label"
                onChange={(focus) => setDraft((current) => ({ ...current, focus }))}
              />
            </label>
            <label className="home-contact-message">
              <span>{isItalian ? "Raccontami qualcosa in più" : "Tell me a little more"}</span>
              <textarea name="message" rows={5} minLength={10} maxLength={2500} required value={draft.message} onChange={(event) => setDraft((current) => ({ ...current, message: event.target.value }))} />
            </label>
            {turnstileSiteKey ? (
              <TurnstileWidget key={turnstileVersion} siteKey={turnstileSiteKey} language={language} onToken={handleTurnstileToken} onError={handleTurnstileError} />
            ) : (
              <p className="contact-form-status error" role="alert">{isItalian ? "Il modulo non è ancora configurato." : "The form is not configured yet."}</p>
            )}
            <button className="home-button dark" type="submit" disabled={submissionStatus === "sending" || !turnstileSiteKey || !turnstileToken}>
              {submissionStatus === "sending" ? (isItalian ? "Invio in corso…" : "Sending…") : (isItalian ? "Invia richiesta" : "Send enquiry")}<ArrowRight size={18} />
            </button>
            {submissionStatus === "success" && <p className="contact-form-status success" role="status">{isItalian ? "Richiesta inviata. Ti risponderò al più presto." : "Enquiry sent. I’ll get back to you as soon as possible."}</p>}
            {submissionStatus === "error" && <p className="contact-form-status error" role="alert">{isItalian ? "Non è stato possibile inviare la richiesta. Riprova tra un momento." : "The enquiry could not be sent. Please try again in a moment."}</p>}
            </form>
            <p className="contact-whatsapp-alternative">{isItalian ? "Preferisci WhatsApp?" : "Prefer WhatsApp?"} <a href={whatsappHref()} target="_blank" rel="noreferrer">{isItalian ? "Scrivimi qui" : "Message me there"}<WhatsAppIcon /></a></p>
          </div>
        </div>
      </main>

      <footer className="home-footer">
        <span>© {new Date().getFullYear()} Mattia D’Elia</span><span>{text(homeMessages.vat)}</span>
      </footer>
    </div>
  );
}
